import {AuthProviderType} from "../domain/providers";
import {getMixpanelProjectToken} from "../env/env";

export type AuthMethod = "email" | "google" | "apple" | "microsoft" | "facebook" | "clever" | "classlink";
export type SignUpMethod = Exclude<AuthMethod, "classlink">;
export type AuthIntent = "signup" | "login";

type PendingAuthIntent = {
  intent: AuthIntent
  method: AuthMethod
  ts: number
}

type MixpanelClient = {
  track?: (eventName: string, properties?: Record<string, unknown>) => void
  identify?: (distinctId: string) => void
  reset?: () => void
  init?: (token: string, config?: Record<string, unknown>, name?: string) => void
  people?: Record<string, unknown>
  push?: (entry: unknown) => number
  _i?: unknown[]
  __SV?: number
  [key: string]: unknown
}

type MixpanelDiag = {
  initAttempted: boolean
  tokenPresent: boolean
  initialised: boolean
  sdkScriptInjected: boolean
  sdkScriptLoaded: boolean
  sdkScriptError: boolean
  queuedCalls: number
  lastTrackEvent?: string
  reason?: string
}

declare global {
  interface Window {
    mixpanel?: MixpanelClient
    __mixpanelDiag?: MixpanelDiag
  }
}

const pendingAuthIntentKey = "wakelet-pending-auth-intent";
const identifiedMixpanelUserKey = "wakelet-mixpanel-identified-user";
const tenMinutes = 10 * 60 * 1000;
let mixpanelInitialised = false;

const getMixpanelDiag = (): MixpanelDiag => {
  if (!window.__mixpanelDiag) {
    window.__mixpanelDiag = {
      initAttempted: false,
      tokenPresent: false,
      initialised: false,
      sdkScriptInjected: false,
      sdkScriptLoaded: false,
      sdkScriptError: false,
      queuedCalls: 0
    };
  }
  return window.__mixpanelDiag;
};

const isMixpanelDebugEnabled = (): boolean => {
  const queryValue = new URLSearchParams(window.location.search).get("mpdebug");
  if (queryValue === "1") {
    return true;
  }
  try {
    return window.localStorage.getItem("mpdebug") === "1";
  } catch {
    return false;
  }
};

const debugMixpanel = (message: string, details?: Record<string, unknown>) => {
  if (!isMixpanelDebugEnabled()) {
    return;
  }
  if (details) {
    console.info(`[mixpanel] ${message}`, details);
    return;
  }
  console.info(`[mixpanel] ${message}`);
};

const readSessionStorage = (key: string): string | undefined => {
  try {
    const value = window.sessionStorage.getItem(key);
    return value === null ? undefined : value;
  } catch {
    debugMixpanel("sessionStorage read failed", {key});
    return;
  }
};

const writeSessionStorage = (key: string, value: string) => {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    debugMixpanel("sessionStorage write failed", {key});
  }
};

const removeSessionStorage = (key: string) => {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    debugMixpanel("sessionStorage remove failed", {key});
  }
};

const readLocalStorage = (key: string): string | undefined => {
  try {
    const value = window.localStorage.getItem(key);
    return value === null ? undefined : value;
  } catch {
    debugMixpanel("localStorage read failed", {key});
    return;
  }
};

const writeLocalStorage = (key: string, value: string) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    debugMixpanel("localStorage write failed", {key});
  }
};

const removeLocalStorage = (key: string) => {
  try {
    window.localStorage.removeItem(key);
  } catch {
    debugMixpanel("localStorage remove failed", {key});
  }
};

const installMixpanelStub = (): MixpanelClient => {
  const existing = window.mixpanel;
  const diag = getMixpanelDiag();
  if (existing?.__SV) {
    debugMixpanel("mixpanel already present");
    return existing;
  }

  const mixpanel = existing || ([] as unknown as MixpanelClient);
  window.mixpanel = mixpanel;
  mixpanel._i = mixpanel._i || [];
  debugMixpanel("installing mixpanel stub");

  mixpanel.init = (token, config, name) => {
    const enqueue = (target: MixpanelClient, methodPath: string) => {
      const parts = methodPath.split(".");
      let method = methodPath;
      if (parts.length === 2) {
        const scope = parts[0];
        method = parts[1];
        target[scope] = target[scope] || {};
        target = target[scope] as MixpanelClient;
      }
      target[method] = (...args: unknown[]) => {
        if (!target.push) {
          return;
        }
        target.push([method, ...args]);
      };
    };

    let target = mixpanel;
    let instanceName = name;
    if (typeof instanceName !== "undefined") {
      mixpanel[instanceName] = mixpanel[instanceName] || [];
      target = mixpanel[instanceName] as MixpanelClient;
    } else {
      instanceName = "mixpanel";
    }

    target.people = target.people || {};
    target.toString = () => {
      let label = "mixpanel";
      if (instanceName !== "mixpanel") {
        label += `.${instanceName}`;
      }
      return `${label} (stub)`;
    };
    target.people.toString = () => `${target.toString?.()}.people (stub)`;

    const methods = "disable time_event track track_pageview track_links track_forms register register_once alias unregister identify name_tag set_config reset people.set people.set_once people.unset people.increment people.append people.union people.track_charge people.clear_charges people.delete_user people.remove";
    methods.split(" ").forEach((method) => enqueue(target, method));
    mixpanel._i?.push([token, config, instanceName]);
    diag.queuedCalls = mixpanel._i?.length || 0;
    debugMixpanel("mixpanel init queued", {queuedCalls: diag.queuedCalls});
  };
  mixpanel.__SV = 1.2;

  const sdkScript = document.createElement("script");
  sdkScript.type = "text/javascript";
  sdkScript.async = true;
  sdkScript.src = "https://cdn.mxpnl.com/libs/mixpanel-2-latest.min.js";
  sdkScript.onload = () => {
    diag.sdkScriptLoaded = true;
    debugMixpanel("mixpanel sdk script loaded");
  };
  sdkScript.onerror = () => {
    diag.sdkScriptError = true;
    debugMixpanel("mixpanel sdk script failed to load");
  };
  const firstScript = document.getElementsByTagName("script")[0];
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(sdkScript, firstScript);
  } else {
    document.head.appendChild(sdkScript);
  }
  diag.sdkScriptInjected = true;
  debugMixpanel("mixpanel sdk script injected");

  return mixpanel;
};

export const initMixpanel = () => {
  const diag = getMixpanelDiag();
  diag.initAttempted = true;
  if (mixpanelInitialised) {
    debugMixpanel("mixpanel already initialised");
    return;
  }
  const token = getMixpanelProjectToken();
  diag.tokenPresent = !!token;
  debugMixpanel("initialising mixpanel", {tokenPresent: diag.tokenPresent});
  if (!token) {
    diag.reason = "missing_project_token";
    debugMixpanel("mixpanel init skipped", {reason: diag.reason});
    return;
  }
  const mixpanel = installMixpanelStub();
  mixpanel.init?.(token, {
    api_host: window.location.origin,
    api_method: "POST",
    autocapture: false,
    record_sessions_percent: 0
  });
  mixpanelInitialised = true;
  diag.initialised = true;
  diag.reason = undefined;
  debugMixpanel("mixpanel initialised");
};

export const beginAuthIntent = (intent: AuthIntent, method: AuthMethod) => {
  const pendingAuthIntent: PendingAuthIntent = {intent, method, ts: Date.now()};
  writeSessionStorage(pendingAuthIntentKey, JSON.stringify(pendingAuthIntent));
};

export const consumeAuthIntent = (maxAgeMs = tenMinutes): PendingAuthIntent | undefined => {
  const value = readSessionStorage(pendingAuthIntentKey);
  removeSessionStorage(pendingAuthIntentKey);
  if (!value) {
    return;
  }
  try {
    const intent = JSON.parse(value) as PendingAuthIntent;
    if (!intent.ts || Date.now() - intent.ts > maxAgeMs) {
      return;
    }
    return intent;
  } catch (err) {
    console.warn("unable to parse pending auth intent", err);
    return;
  }
};

export const clearAuthIntent = () => {
  removeSessionStorage(pendingAuthIntentKey);
}

const trackMixpanel = (event: string, properties?: Record<string, unknown>) => {
  const diag = getMixpanelDiag();
  diag.lastTrackEvent = event;
  if (!window.mixpanel?.track) {
    debugMixpanel("track skipped (track fn unavailable)", {event});
    return;
  }
  try {
    debugMixpanel("track called", {event});
    window.mixpanel.track(event, properties);
  } catch (err) {
    console.warn("mixpanel tracking failed", err);
  }
};

export const trackSignUpInitialised = (method: SignUpMethod) => {
  trackMixpanel("sign_up_initialised", {method});
};

export const trackSignUpSubmitted = (method: SignUpMethod) => {
  trackMixpanel("sign_up_submitted", {method});
};

export const trackSignUpCompleted = (method: SignUpMethod) => {
  trackMixpanel("sign_up_completed", {method});
};

export const trackSignUpAgeGateHit = (method: SignUpMethod) => {
  trackMixpanel("sign_up_age_gate_hit", {method});
};

export const trackLogIn = (method: AuthMethod) => {
  trackMixpanel("log_in", {method});
};

export const trackAccountLinkRequested = (method: AuthMethod) => {
  trackMixpanel("account_link_requested", {method});
};

export const identifyMixpanelUser = (userId: string) => {
  if (!userId || !window.mixpanel?.identify) {
    return;
  }
  try {
    debugMixpanel("identify called", {userId});
    window.mixpanel.identify(userId);
    writeLocalStorage(identifiedMixpanelUserKey, userId);
  } catch (err) {
    console.warn("mixpanel identify failed", err);
  }
};

export const hasIdentifiedMixpanelUser = (): boolean => !!readLocalStorage(identifiedMixpanelUserKey);

export const resetMixpanelUser = () => {
  if (!window.mixpanel?.reset) {
    removeLocalStorage(identifiedMixpanelUserKey);
    return;
  }
  try {
    debugMixpanel("reset called");
    window.mixpanel.reset();
  } catch (err) {
    console.warn("mixpanel reset failed", err);
  }
  removeLocalStorage(identifiedMixpanelUserKey);
};

export const trackPendingAuthSuccess = () => {
  const intent = consumeAuthIntent();
  if (!intent) {
    return;
  }
  if (intent.intent === "signup") {
    const signUpMethod = toSignUpMethod(intent.method);
    if (!signUpMethod) {
      return;
    }
    trackSignUpSubmitted(signUpMethod);
    return;
  }
  trackLogIn(intent.method);
};

export const toAuthMethod = (provider: AuthProviderType | string): AuthMethod | undefined => {
  switch (provider) {
    case AuthProviderType.Password:
    case "password":
    case "email":
      return "email";
    case AuthProviderType.Google:
    case "google":
      return "google";
    case AuthProviderType.Apple:
    case "apple":
      return "apple";
    case AuthProviderType.Microsoft:
    case "microsoft":
      return "microsoft";
    case AuthProviderType.Facebook:
    case "facebook":
      return "facebook";
    case AuthProviderType.Clever:
    case "clever":
      return "clever";
    case AuthProviderType.ClassLink:
    case "classlink":
      return "classlink";
    default:
      return;
  }
};

export const toSignUpMethod = (method: AuthMethod): SignUpMethod | undefined => {
  if (method === "classlink") {
    return;
  }
  return method;
}
